import { atom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { FieldPath } from 'react-hook-form';
import * as yup from 'yup';
import { field2ToFormField, FieldEntity } from '../../../data-models/field2.data-model';
import {
  CompanyFundMetricsDataModel,
  CompanyFundMetricsField,
  companyFundMetricsFields,
} from '../../../schemas/CompanyFundMetrics.schema';
import { fundsAtom, fundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { getForesightStore } from '../../../util/jotai-store';
import { schemaToFormFields } from '../../../util/schema-utils';
import { IFormField } from '../../../view-models/form.view-model';
import { filterAndSortByPreferences } from '../utils/filterAndSortByPreferences';
import { customFieldsByEntityAtom } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import {
  IMetricsFundTypeDataModel,
  IMetricFundDataModel,
  createMetricsFundDataModel,
} from '../../../data-models/metrics.data-model';
import { captureUnexpectedState } from '../../../util/SentryUtils';
import { createFundDataModel, IFundDataModel } from '../../../data-models/fund.data-model';
import { FundDataModel } from '../../../schemas/Fund.schema';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { selectedColumnsGridDisplayFieldsOrgState } from '../../AdminPanel/DataDisplay/DataDisplayState';
import { columnNameToPathState } from '../../PortfolioOverview/state/ViewStateJ';
import { filterRestrictedColumns } from '../../../components/ColumnManagement/utils';

export const AllFundsId = -1;

const FundTypeNameToNumericIdAtom = atom<Map<string, number>>((get) => {
  const allFunds = get(fundsAtom);
  return createFundTypeNameToNumericIdMapping(allFunds);
});

const ALL_FUNDS_FIELDS = ['moic', 'ownerShipPercentage', 'irr', 'amountInvested', 'fmv', 'distributions'];

const ALL_SINGLE_FUND_FIELDS: (keyof CompanyFundMetricsDataModel)[] = [
  'moic',
  'ownerShipPercentage',
  'irr',
  'amountInvested',
  'fmv',
  'reserves',
  'exitDate',
  'exitValuation',
  'exitType',
  'initialPreMoney',
  'exitOwnershipPercentage',
  'entryOwnership',
  'distributions',
  'convertedOwnership',
  'percentOfFund',
  'rtfe',
  'initialInvestmentDate',
  'totalValue',
  'fundId',
  'noOfShares',
];

const ALL_DISABLED_FIELDS = [
  'moic',
  'ownerShipPercentage',
  'irr',
  'fmv',
  'distributions',
  'amountInvested',
  'convertedOwnership',
  'percentOfFund',
  'rtfe',
  'noOfShares',
  'totalValue',
] as const;

export function useInvestmentMetricsFields(
  fieldsToShow?: FieldPath<CompanyFundMetricsDataModel>[],
  selectedFund?: number
) {
  const allFields = useAllInvestmentMetricsFields(selectedFund);
  return useMemo(() => {
    const res = !fieldsToShow?.length ? allFields : filterAndSortByPreferences(allFields, fieldsToShow);
    setDisabledFields(res, selectedFund);

    return res;
  }, [allFields, fieldsToShow, selectedFund]);
}

export function useAllInvestmentMetricsFields(selectedFund?: number) {
  const columnsToHide = useAtomValue(selectedColumnsGridDisplayFieldsOrgState);
  const columnNameToPathMap = useAtomValue(columnNameToPathState(COMPANY_VIEW_TYPE.RETURN_FORECAST));
  const customFields = useAtomValue(customFieldsByEntityAtom);
  const fundTypeNameToNumericId = useAtomValue(FundTypeNameToNumericIdAtom);
  const fundTypeIds = useMemo(() => new Set(fundTypeNameToNumericId.values()), [fundTypeNameToNumericId]);

  return useMemo(() => {
    if (selectedFund === AllFundsId || fundTypeIds.has(selectedFund ?? 0)) {
      const filteredFields = filterRestrictedColumns(ALL_FUNDS_FIELDS, columnsToHide, columnNameToPathMap);
      const baseSchema = yup
        .object(companyFundMetricsFields())
        .pick(filteredFields as CompanyFundMetricsField[]);

      return schemaToFormFields(baseSchema);
    } else {
      const fields = companyFundMetricsFields();
      const { fundId, ...rest } = fields;
      const fundIdField = fundId.label('Fund').customMeta({
        formatter: 'fund',
      });
      const filteredFields = filterRestrictedColumns(
        ALL_SINGLE_FUND_FIELDS,
        columnsToHide,
        columnNameToPathMap
      );

      const baseSchema = yup
        .object({ ...rest, fundId: fundIdField })
        .pick(filteredFields as CompanyFundMetricsField[]);
      const companyCustomFields =
        customFields.get(FieldEntity.fundCompany)?.map((field) => field2ToFormField(field)) ?? [];
      const fundCompanyFields = schemaToFormFields(baseSchema);
      return fundCompanyFields.concat(companyCustomFields);
    }
  }, [customFields, selectedFund, fundTypeIds, columnsToHide, columnNameToPathMap]);
}

export function setDisabledFields(
  fields: IFormField<unknown>[],
  selectedFund?: number,
  fundsById: Map<number, IFundDataModel> = getForesightStore().get(fundsByIdMapAtom)
) {
  const fundTypeIds = new Set(getForesightStore().get(FundTypeNameToNumericIdAtom).values());
  if (selectedFund === AllFundsId || fundTypeIds.has(selectedFund ?? 0)) {
    fields.forEach((field) => {
      field.disabled = true;
    });
    return;
  }
  const systemFields = new Set<string>(ALL_SINGLE_FUND_FIELDS);
  if (fundsById.get(selectedFund ?? -1)?.fundKind === 'family') {
    // Family system fields are member aggregates; only family x company custom fields are editable.
    fields.forEach((field) => {
      field.disabled = systemFields.has(field.key);
    });
    return;
  }
  const disabledFields: Set<CompanyFundMetricsField> = new Set(ALL_DISABLED_FIELDS);
  fields.forEach((field) => {
    field.disabled = disabledFields.has(field.key as CompanyFundMetricsField);
  });
}
export function fundTypeDataToFundData(
  fundTypeData: IMetricsFundTypeDataModel,
  companyId: number
): IMetricFundDataModel | null {
  const fundTypeNameToNumericId = getForesightStore().get(FundTypeNameToNumericIdAtom);
  const numericId = fundTypeNameToNumericId.get(fundTypeData.fundTypeId);
  if (!numericId) {
    captureUnexpectedState('fundTypeDataToFundData', {
      fundTypeData,
      fundTypeNameToNumericId,
    });
    return null;
  }

  return createMetricsFundDataModel({
    ...fundTypeData,
    companyId,
    fundId: numericId,
    id: numericId,
  });
}
export function getFundTypeOptions(investmentsByFund: Map<number, IMetricFundDataModel>) {
  const options: FundDataModel[] = [];
  const fundTypeNameToNumericId = getForesightStore().get(FundTypeNameToNumericIdAtom);
  fundTypeNameToNumericId.forEach((id, name) => {
    if (investmentsByFund.has(id)) {
      // Suffix distinguishes the rollup from a fund of the same name (fund families named after their flagship fund)
      options.push(createFundDataModel({ name: `${name} (Combined)`, id }));
    }
  });
  return options;
}

export function createFundTypeNameToNumericIdMapping(allFunds: FundDataModel[]): Map<string, number> {
  const reservedIds = allFunds.map((fund) => fund.id).concat([AllFundsId]);
  const res = new Map<string, number>();
  const fundGroups = allFunds.reduce((acc, fund) => {
    if (fund.fundType) {
      acc.add(fund.fundType);
    }
    return acc;
  }, new Set<string>());
  let id = 1;
  fundGroups.forEach((fundGroup) => {
    while (reservedIds.includes(id)) {
      id++;
    }
    res.set(fundGroup, id);
    id++;
  });

  return res;
}
